/**
 * Booking Flow Page
 *
 * Main container for the 7-stage customer booking flow.
 * Manages stage navigation, booking state, and API interactions.
 *
 * Location: src/pages/BookingFlowPage.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import * as bookingHoldsService from '../../services/bookingHoldsService';
import * as api from '../../utils/apiClient';
import { createBookingQuote, getActivePromotions, verifyQuote, finalizeQuote, getPricingSummary } from '../../services/pricingApi';
import PriceBreakdown from '../../components/pricing/PriceBreakdown';
import PriceChangeBanner from '../../components/pricing/PriceChangeBanner';
import PromoCodeInput from '../../components/pricing/PromoCodeInput';
import PriceSummaryBar, { PriceSummaryBarSpacer } from '../../components/pricing/PriceSummaryBar';

function isConsecutiveBookingEnabled() {
  return import.meta.env.VITE_FEATURE_CONSECUTIVE_BOOKING === 'true';
}
import SimplifiedHeader from '../../components/common/SimplifiedHeader'; // SCRUM-338
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import {
  BookingProgressIndicator,
  PricingSummary,
  Stage1DateSelection,
  Stage2GuestInfo,
  Stage3Contact,
  Stage4Age,
  Stage5Requirements,
  Stage6Review,
  Stage7Payment,
} from '../../components/booking/flow';

/**
 * Booking Flow Page Component
 * Orchestrates the 7-stage booking process
 */
export default function BookingFlowPage() {
  const { holdId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCart();

  // Booking state
  // Start at Stage 1 (Date Selection) to allow users to review/modify date and guest count
  const [currentStage, setCurrentStage] = useState(1);
  const [highestCompletedStage, setHighestCompletedStage] = useState(0);

  // SCRUM-916: Server-side booking state
  const [bookingId, setBookingId] = useState(null);
  const [stageLoading, setStageLoading] = useState(false);
  const [stageError, setStageError] = useState(null);
  const bookingInitAttempted = useRef(false);

  // SCRUM-1278: Server-driven pricing quote state
  const [quoteId, setQuoteId] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);

  // SCRUM-2057: Tax amount from pricing snapshot
  const [taxAmountCents, setTaxAmountCents] = useState(0);

  // SCRUM-1279: Promotion and pricing component state
  const [autoAppliedPromo, setAutoAppliedPromo] = useState(null);
  const [autoAppliedSavings, setAutoAppliedSavings] = useState(0);
  const [_appliedPromoCode, _setAppliedPromoCode] = useState(null);

  // SCRUM-1280: Quote verification state
  const [verificationResult, setVerificationResult] = useState(null);

  // SCRUM-335, SCRUM-336: Fixed NaN guest count propagation
  // SCRUM-1278: Price no longer read from URL params — comes from server quote
  const initializeBookingData = () => {
    const guests = parseInt(searchParams.get('guests')) || 1;

    return {
      holdId,
      experienceId: searchParams.get('experienceId'),
      guideId: searchParams.get('guideId'),
      date: searchParams.get('date') || searchParams.get('startDate'),
      startDate: searchParams.get('date') || searchParams.get('startDate'), // For Stage 1 component
      endDate: searchParams.get('endDate') || null, // MVP 1.4.2: Multi-day support
      guests: guests,
      partySize: guests, // For Stage 1 component
      minimumAge: parseInt(searchParams.get('minimumAge')) || 0,
    };
  };

  const [bookingData, setBookingData] = useState(initializeBookingData());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hold, setHold] = useState(null);

  // Validate hold on mount
  useEffect(() => {
    async function checkHold() {
      if (!holdId) {
        setError('No hold ID provided');
        setLoading(false);
        return;
      }

      try {
        const result = await bookingHoldsService.validateHold(holdId);

        if (result.success && result.valid) {
          setHold(result.hold);
          setLoading(false);

          // SCRUM-709: Sync holdId to cart for expiry detection
          if (isConsecutiveBookingEnabled() && result.hold) {
            cart.setHoldId(holdId, result.hold.expires_at);
          }

          // Auto-advance to Stage 2 if date and guests are already selected
          if (bookingData.startDate && bookingData.partySize) {
            setCurrentStage(2);
            setHighestCompletedStage(1);
          }
        } else {
          console.error('[BookingFlowPage] Hold validation failed:', result);
          setError(result.message || 'Hold is no longer valid');
          setLoading(false);
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (err) {
        console.error('[BookingFlowPage] Hold validation error:', err);
        setError('Failed to validate booking hold');
        setLoading(false);
        setTimeout(() => navigate('/'), 3000);
      }
    }

    checkHold();
  }, [holdId, navigate, bookingData.partySize, bookingData.startDate, cart]);

  // SCRUM-1278: Fetch pricing quote from server after hold is validated
  useEffect(() => {
    if (!hold || !bookingData.experienceId || quoteId) {
      return; // Wait for hold validation, need experienceId, and don't re-fetch
    }

    const fetchQuote = async () => {
      setQuoteLoading(true);
      setQuoteError(null);
      try {
        const result = await createBookingQuote(bookingData.experienceId, {
          tripDate: bookingData.startDate || bookingData.date,
          partySize: bookingData.partySize || bookingData.guests || 1,
        });
        // apiRequest returns { success, data: { quoteId, totalCents, ... } }
        const quote = result.data;
        setQuoteId(quote.quoteId);
        setQuoteData(quote);
        setBookingData(prev => ({ ...prev, quoteId: quote.quoteId }));
      } catch (err) {
        setQuoteError(
          err?.message || 'Unable to load pricing. Please try again.'
        );
      } finally {
        setQuoteLoading(false);
      }
    };

    fetchQuote();
  }, [hold, bookingData.experienceId, bookingData.startDate, bookingData.date, bookingData.partySize, bookingData.guests, quoteId]);

  // SCRUM-1279: Auto-apply public promotion on mount (AC-05)
  useEffect(() => {
    if (!bookingData.experienceId || !quoteId || autoAppliedPromo) {
      return; // Wait for initial quote, only check once
    }

    const checkAutoPromo = async () => {
      try {
        const result = await getActivePromotions(bookingData.experienceId);
        const promos = result?.data || result;
        const publicPromo = Array.isArray(promos) ? promos[0] : promos;
        if (publicPromo?.promoCode || publicPromo?.code) {
          const code = publicPromo.promoCode || publicPromo.code;
          // Re-quote with promo applied
          const quoteResult = await createBookingQuote(bookingData.experienceId, {
            tripDate: bookingData.startDate || bookingData.date,
            partySize: bookingData.partySize || bookingData.guests || 1,
            promoCode: code,
          });
          const quote = quoteResult.data;
          const savings = (quoteData?.totalCents || 0) - (quote.totalCents || 0);
          setQuoteId(quote.quoteId);
          setQuoteData(quote);
          setBookingData(prev => ({ ...prev, quoteId: quote.quoteId }));
          setAutoAppliedPromo(publicPromo);
          setAutoAppliedSavings(Math.max(0, savings));
          _setAppliedPromoCode(code);
        }
      } catch (_err) {
        // Silent — failed promotion check must never break the booking flow
      }
    };

    checkAutoPromo();
  }, [bookingData.experienceId, quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SCRUM-1279: Handle promo code applied from PromoCodeInput (AC-03)
  const handlePromoApplied = useCallback(async (code, _promo, _savings) => {
    try {
      setQuoteLoading(true);
      setQuoteError(null);
      const result = await createBookingQuote(bookingData.experienceId, {
        tripDate: bookingData.startDate || bookingData.date,
        partySize: bookingData.partySize || bookingData.guests || 1,
        promoCode: code,
      });
      const quote = result.data;
      setQuoteId(quote.quoteId);
      setQuoteData(quote);
      setBookingData(prev => ({ ...prev, quoteId: quote.quoteId }));
      _setAppliedPromoCode(code);
    } catch (err) {
      setQuoteError(err?.message || 'Failed to apply promo code.');
    } finally {
      setQuoteLoading(false);
    }
  }, [bookingData.experienceId, bookingData.startDate, bookingData.date, bookingData.partySize, bookingData.guests]);

  // SCRUM-1279: Handle promo code removed
  const handlePromoRemoved = useCallback(async () => {
    try {
      setQuoteLoading(true);
      setQuoteError(null);
      _setAppliedPromoCode(null);
      setAutoAppliedPromo(null);
      setAutoAppliedSavings(0);
      // Re-quote without promo
      const result = await createBookingQuote(bookingData.experienceId, {
        tripDate: bookingData.startDate || bookingData.date,
        partySize: bookingData.partySize || bookingData.guests || 1,
      });
      const quote = result.data;
      setQuoteId(quote.quoteId);
      setQuoteData(quote);
      setBookingData(prev => ({ ...prev, quoteId: quote.quoteId }));
    } catch (err) {
      setQuoteError(err?.message || 'Failed to update pricing.');
    } finally {
      setQuoteLoading(false);
    }
  }, [bookingData.experienceId, bookingData.startDate, bookingData.date, bookingData.partySize, bookingData.guests]);

  // SCRUM-916: Initialize booking from hold when user is authenticated
  // Fires on mount for pre-authenticated users, or after Stage 2 registration
  useEffect(() => {
    if (!holdId || bookingId || bookingInitAttempted.current || !user || loading) {
      return;
    }

    bookingInitAttempted.current = true;

    const createBooking = async () => {
      try {
        const response = await api.post('bookings', {
          holdId,
          num_participants: bookingData.partySize || bookingData.guests || 1,
          // SCRUM-1278: quoteId links to server-calculated pricing — no price from URL
          ...(quoteId && { quote_id: quoteId }),
        });
        const newBookingId = response.booking.id;
        setBookingId(newBookingId);
        setBookingData(prev => ({ ...prev, bookingId: newBookingId }));
      } catch (err) {
        // Non-fatal on mount — will retry in handleNext for Stage 2 if needed
        console.warn('[BookingFlowPage] Booking initialization deferred:', err.message);
        bookingInitAttempted.current = false;
      }
    };

    createBooking();
  }, [holdId, bookingId, user, loading, bookingData.partySize, bookingData.guests, quoteId]); // eslint-disable-line react-hooks/exhaustive-deps

  // SCRUM-2057: Fetch tax from pricing snapshot once booking is created
  useEffect(() => {
    if (!bookingId) { return; }
    getPricingSummary(bookingId)
      .then((result) => {
        if (result?.data?.taxAmountCents > 0) {
          setTaxAmountCents(result.data.taxAmountCents);
        }
      })
      .catch(() => { /* non-fatal — tax line simply hidden */ });
  }, [bookingId]);

  // SCRUM-917: Silent hold timer — no visible countdown per MVP 1.4 spec.
  // Only detects expiry; the "Still Here?" prompt would be wired here when built.
  useEffect(() => {
    if (!hold?.expires_at) { return; }

    const checkExpiry = () => {
      const expiresAtMs = typeof hold.expires_at === 'number'
        ? hold.expires_at
        : new Date(hold.expires_at).getTime();

      if (Date.now() >= expiresAtMs) {
        setError('Your booking hold has expired');
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 5000); // Check every 5s (no countdown needed)

    return () => clearInterval(interval);
  }, [hold]);

  // Track progress action
  const trackProgressAction = useCallback(async (step, actionType) => {
    if (!holdId) {return;}

    try {
      const result = await bookingHoldsService.extendHold(holdId, step, actionType);
      // Update hold state with new expiration time so timer resets
      if (result.success && result.data) {
        setHold(result.data);
      }
    } catch (err) {
      console.error('Failed to extend hold:', err);
    }
  }, [holdId]);

  // Note: Authentication is handled in Stage 2 (Guest Info)
  // Anonymous users can start the booking flow and create an account during the process

  // SCRUM-1278: Pricing from server quote — not URL params
  const guests = bookingData.partySize || bookingData.guests || 1;
  const subtotal = quoteData?.subtotalCents || 0;
  const platformFee = quoteData?.platformFeeCents || 0;
  const totalPrice = quoteData?.totalCents || 0;
  // Per-person price for display (derived from quote base rate)
  const pricePerPerson = quoteData?.baseRateCents || 0;

  // Stage navigation handlers
  const handleNext = async (stageData) => {
    // Prevent double-submission while a stage API call is in flight
    if (stageLoading) { return; }

    // Clear any previous stage error
    setStageError(null);

    // Update booking data with stage data
    setBookingData(prev => ({ ...prev, ...stageData }));

    // Track progress action
    trackProgressAction(currentStage, 'step_advanced');

    // SCRUM-916: Stage-specific server calls before advancing
    let activeBookingId = bookingId;

    // --- Stage 2: Create booking (if not yet created) + account bridge ---
    if (currentStage === 2) {
      if (!activeBookingId) {
        setStageLoading(true);
        try {
          const response = await api.post('bookings', {
            holdId,
            num_participants: bookingData.partySize || bookingData.guests || 1,
            // SCRUM-1278: quoteId links to server-calculated pricing — no price from URL
            ...(quoteId && { quote_id: quoteId }),
          });
          activeBookingId = response.booking.id;
          setBookingId(activeBookingId);
          setBookingData(prev => ({ ...prev, bookingId: activeBookingId }));
        } catch (err) {
          setStageError(
            err.message?.includes('expired')
              ? 'Your hold has expired. Please select a new date.'
              : 'Unable to initialize booking. Please try again.'
          );
          setStageLoading(false);
          return; // BLOCK — cannot proceed without booking
        }
        setStageLoading(false);
      }

      // Account bridge (non-blocking)
      if (activeBookingId) {
        api.put(`bookings/${activeBookingId}/account`).catch(err => {
          console.warn('Account bridge call failed:', err.message);
        });
      }
    }

    // --- Stage 3: SMS consent (TCPA-critical — BLOCKS on failure) ---
    if (currentStage === 3 && activeBookingId) {
      setStageLoading(true);
      try {
        if (stageData.smsConsent) {
          await api.put(`bookings/${activeBookingId}/contact`, {
            phone_number: stageData.phoneNumber,
            consent_given: true,
          });
        } else {
          await api.post(`bookings/${activeBookingId}/skip-sms`);
        }
      } catch (err) {
        setStageError(
          err.message?.includes('out of order') || err.message?.includes('409')
            ? 'This step is out of order. Please restart the booking.'
            : 'Unable to save your contact information. Please try again.'
        );
        setStageLoading(false);
        return; // BLOCK advancement
      }
      setStageLoading(false);
    }

    // --- Stage 4: Age verification (conditional — BLOCKS on failure) ---
    if (currentStage === 4 && activeBookingId) {
      const minimumAge = stageData.minimumAge;
      setStageLoading(true);
      try {
        if (stageData.ageVerified) {
          await api.put(`bookings/${activeBookingId}/age-verify`, {
            minimum_age_required: minimumAge,
            accepted: true,
          });
        } else {
          await api.post(`bookings/${activeBookingId}/skip-age-verification`);
        }
      } catch (err) {
        setStageError(
          err.message?.includes('out of order') || err.message?.includes('409')
            ? 'This step is out of order. Please restart the booking.'
            : 'Unable to save age verification. Please try again.'
        );
        setStageLoading(false);
        return; // BLOCK advancement
      }
      setStageLoading(false);
    }

    // --- Stage 5: Requirements (non-blocking fire-and-forget) ---
    if (currentStage === 5 && activeBookingId) {
      api.put(`bookings/${activeBookingId}/requirements`, {
        requirements: {
          dietary_restrictions: stageData.dietaryRestrictions || bookingData.dietaryRestrictions || '',
          questions_for_guide: stageData.questionsForGuide || bookingData.questionsForGuide || '',
        },
      }).catch(err => {
        console.warn('Requirements save failed (non-blocking):', err.message);
      });
    }

    // --- Stage 6: Policy acceptance (legal-critical — BLOCKS on failure) ---
    if (currentStage === 6 && activeBookingId) {
      setStageLoading(true);
      try {
        await api.put(`bookings/${activeBookingId}/accept-policies`, {
          terms_accepted: bookingData.termsAccepted || false,
          cancellation_policy_accepted: bookingData.policyAccepted || false,
        });
      } catch (err) {
        const msg = err.message || '';
        setStageError(
          msg.includes('must be true')
            ? 'You must accept both the Terms of Service and Cancellation Policy to continue.'
            : msg.includes('out of order') || msg.includes('409')
              ? 'This step is out of order. Please restart the booking.'
              : 'Unable to record your acceptance. Please try again.'
        );
        setStageLoading(false);
        return; // BLOCK advancement
      }
      setStageLoading(false);
    }

    // --- SCRUM-1280: Quote verification before advancing to payment ---
    if (currentStage === 6 && quoteId) {
      setStageLoading(true);
      setVerificationResult(null);
      try {
        const result = await verifyQuote(quoteId);
        const data = result.data || result;

        // Handle expired or not-found quotes
        if (data.error === 'QUOTE_EXPIRED') {
          setVerificationResult({ error: 'QUOTE_EXPIRED', originalTotalCents: data.originalTotalCents });
          setStageLoading(false);
          return; // BLOCK — show expired banner with refresh option
        }
        if (data.error === 'QUOTE_NOT_FOUND') {
          setVerificationResult({ error: 'QUOTE_NOT_FOUND' });
          setStageLoading(false);
          return; // BLOCK
        }

        const comparison = data.comparison;
        if (comparison) {
          if (comparison.status === 'match') {
            // Price unchanged — proceed to Stage 7
            setVerificationResult(null);
          } else if (comparison.status === 'increased') {
            // Price increased — show banner, block until re-confirm
            setVerificationResult({
              comparison,
              originalTotalCents: data.originalQuote?.totalCents,
              freshTotalCents: data.freshPricing?.totalCents,
            });
            setStageLoading(false);
            return; // BLOCK — customer must re-confirm
          } else if (comparison.status === 'decreased') {
            // Price decreased — show banner briefly, proceed
            setVerificationResult({
              comparison,
              originalTotalCents: data.originalQuote?.totalCents,
              freshTotalCents: data.freshPricing?.totalCents,
            });
            // Proceed to Stage 7 (banner visible but non-blocking)
          }
        }
      } catch (err) {
        setStageError(
          err?.message || 'Unable to verify pricing. Please try again.'
        );
        setStageLoading(false);
        return; // BLOCK
      }
      setStageLoading(false);
    }

    // If completing Stage 7 (Payment), redirect to confirmation page
    if (currentStage === 7 && stageData.paymentCompleted) {
      // SCRUM-1311: Finalize quote after successful checkout (AC-07)
      // Fire-and-forget — failure must NOT block checkout success
      if (quoteId && activeBookingId) {
        finalizeQuote(quoteId, activeBookingId).catch(err => {
          console.warn('[BookingFlowPage] finalizeQuote failed after successful checkout:', err?.message);
        });
      }

      // SCRUM-709: Cart checkout success — navigate with cartId (AC-10)
      if (stageData.cartId) {
        cart.markCompleted();
        const confirmationUrl = new URL('/booking/confirmation', window.location.origin);
        confirmationUrl.searchParams.set('cartId', stageData.cartId);
        confirmationUrl.searchParams.set('title', experienceData.name);
        confirmationUrl.searchParams.set('guide', experienceData.guideName);
        confirmationUrl.searchParams.set('guests', bookingData.partySize || bookingData.guests);
        confirmationUrl.searchParams.set('email', bookingData.customerEmail || bookingData.email);
        navigate(confirmationUrl.pathname + confirmationUrl.search);
        return;
      }

      // Build confirmation URL with booking details (existing single-booking path)
      const confirmationUrl = new URL('/booking/confirmation', window.location.origin);
      // SCRUM-710: Pass bookingId so confirmation page can fetch from API (PG7 fix)
      if (activeBookingId) {
        confirmationUrl.searchParams.set('bookingId', activeBookingId);
      }
      if (stageData.confirmationNumber) {
        confirmationUrl.searchParams.set('confirmation', stageData.confirmationNumber);
      }
      confirmationUrl.searchParams.set('title', experienceData.name);
      confirmationUrl.searchParams.set('guide', experienceData.guideName);
      confirmationUrl.searchParams.set('date', bookingData.startDate || bookingData.date);
      if (bookingData.endDate) {
        confirmationUrl.searchParams.set('endDate', bookingData.endDate);
      }
      confirmationUrl.searchParams.set('guests', bookingData.partySize || bookingData.guests);
      confirmationUrl.searchParams.set('duration', experienceData.duration);
      confirmationUrl.searchParams.set('email', bookingData.customerEmail || bookingData.email);

      // Navigate to confirmation page
      navigate(confirmationUrl.pathname + confirmationUrl.search);
      return;
    }

    // Move to next stage and update highest completed
    setCurrentStage(prev => prev + 1);
    setHighestCompletedStage(prev => Math.max(prev, currentStage));
  };

  // SCRUM-1280: Re-confirm after price increase — proceed to Stage 7
  const handleVerificationContinue = useCallback(() => {
    setVerificationResult(null);
    setCurrentStage(prev => prev + 1);
    setHighestCompletedStage(prev => Math.max(prev, 6));
  }, []);

  // SCRUM-1280: Go back from price increase banner
  const handleVerificationGoBack = useCallback(() => {
    setVerificationResult(null);
  }, []);

  // SCRUM-1280: Refresh expired quote — navigate to experience page
  const handleExpiredRefresh = useCallback(() => {
    const experienceId = bookingData.experienceId;
    navigate(experienceId ? `/experiences/${experienceId}` : '/');
  }, [bookingData.experienceId, navigate]);

  const handleBack = async () => {
    setStageError(null); // Clear stage error on back navigation
    if (currentStage > 1) {
      // Navigate back within the booking flow
      setCurrentStage(prev => prev - 1);
    } else if (currentStage === 1) {
      // Release the hold before going back to experience page
      try {
        await bookingHoldsService.releaseHold(holdId);
      } catch (err) {
        console.error('Failed to release hold:', err);
        // Continue navigation even if release fails
      }
      // Go back to the experience page
      navigate(-1);
    }
  };

  const handleStageClick = async (stageNum) => {
    // Allow navigation to any completed stage OR any stage up to current stage
    // This enables editing: if you're on stage 5 and go back to stage 2, you can navigate back to stage 5
    const maxAccessibleStage = Math.max(highestCompletedStage, currentStage);
    if (stageNum >= 1 && stageNum <= maxAccessibleStage && stageNum !== currentStage) {
      setCurrentStage(stageNum);
    }
  };

  // Stage configuration
  const stages = [
    { number: 1, name: 'Date Selection', component: Stage1DateSelection },
    { number: 2, name: 'Guest Info', component: Stage2GuestInfo },
    { number: 3, name: 'Contact', component: Stage3Contact },
    { number: 4, name: 'Age Verification', component: Stage4Age },
    { number: 5, name: 'Requirements', component: Stage5Requirements },
    { number: 6, name: 'Review', component: Stage6Review },
    { number: 7, name: 'Payment', component: Stage7Payment },
  ];

  const CurrentStageComponent = stages[currentStage - 1]?.component;

  // Experience data for Stage 1 and Stage 6
  const experienceData = {
    name: searchParams.get('title') || 'Your Experience',
    guideName: searchParams.get('guideName') || searchParams.get('guide') || 'Your Guide',
    guidePhoto: searchParams.get('guidePhoto') || null,
    guideRating: null,
    guideReviews: null,
    duration: searchParams.get('duration') || '',
    minGuests: parseInt(searchParams.get('minGuests')) || 1,
    maxGuests: parseInt(searchParams.get('maxGuests')) || 99,
    cancellationPolicy: {
      tier: 'Flexible',
      fullRefundDays: 7,
      partialRefundDays: 3,
      partialRefundPercent: 50,
    },
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner message="Loading booking..." color="border-rose-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md bg-white rounded-lg shadow-lg p-6">
          <Alert variant="error" message={error} className="mb-4" />
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - SCRUM-338: Use SimplifiedHeader for consistency */}
      <SimplifiedHeader
        title="Complete Your Booking"
        onBack={currentStage === 1 ? async () => {
          try {
            await bookingHoldsService.releaseHold(holdId);
          } catch (err) {
            console.error('Failed to release hold:', err);
          }
          navigate(-1);
        } : undefined}
      />

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <BookingProgressIndicator
            currentStage={currentStage}
            highestCompletedStage={highestCompletedStage}
            onStageClick={handleStageClick}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Stage Content */}
          <div className="lg:col-span-2">
            {/* SCRUM-1278: Quote error display — blocks checkout */}
            {quoteError && (
              <Alert variant="error" message={quoteError} className="mb-4" />
            )}

            {/* SCRUM-1280: Quote verification banner */}
            {verificationResult && (
              <PriceChangeBanner
                comparison={verificationResult.comparison}
                originalTotalCents={verificationResult.originalTotalCents}
                freshTotalCents={verificationResult.freshTotalCents}
                error={verificationResult.error}
                onGoBack={handleVerificationGoBack}
                onContinue={handleVerificationContinue}
                onRefresh={handleExpiredRefresh}
                className="mb-4"
              />
            )}

            {/* SCRUM-916: Stage error display */}
            {stageError && (
              <Alert variant="error" message={stageError} className="mb-4" />
            )}

            {/* SCRUM-916: Loading overlay for stage API calls */}
            <div className="relative">
            {stageLoading && (
              <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-lg">
                <LoadingSpinner message="Saving..." color="border-emerald-600" />
              </div>
            )}

            {CurrentStageComponent ? (
              currentStage === 1 ? (
                <div className="bg-white rounded-lg shadow p-6">
                  <CurrentStageComponent
                    experience={experienceData}
                    bookingData={bookingData}
                    onChange={(field, value) => {
                      setBookingData(prev => ({ ...prev, [field]: value }));
                      // Also update the other field names for consistency
                      if (field === 'startDate') {
                        setBookingData(prev => ({ ...prev, date: value }));
                      }
                      if (field === 'partySize') {
                        setBookingData(prev => ({ ...prev, guests: value }));
                      }
                      // MVP 1.4.2: endDate is set directly by Stage1DateSelection for multi-day
                    }}
                    errors={{}}
                    pricing={{
                      perPerson: pricePerPerson,
                      subtotal: subtotal,
                      platformFee: platformFee, // SCRUM-337: Standardized terminology
                      taxAmountCents: taxAmountCents,
                      total: totalPrice,
                    }}
                  />
                  {/* Navigation buttons for Stage 1 - SCRUM-340: Fixed button colors */}
                  <div className="mt-6 flex gap-4">
                    <button
                      onClick={handleBack}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => handleNext({
                        date: bookingData.startDate,
                        guests: bookingData.partySize
                      })}
                      disabled={!bookingData.startDate || !bookingData.partySize || stageLoading || quoteLoading || !!quoteError}
                      className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300"
                    >
                      {quoteLoading ? 'Loading pricing...' : stageLoading ? 'Saving...' : 'Continue'}
                    </button>
                  </div>
                </div>
              ) : currentStage === 6 ? (
                <div className="bg-white rounded-lg shadow p-6">
                  <CurrentStageComponent
                    experience={experienceData}
                    bookingData={bookingData}
                    onChange={(field, value) => {
                      setBookingData(prev => ({ ...prev, [field]: value }));
                    }}
                    errors={{}}
                    pricing={{
                      perPerson: pricePerPerson,
                      subtotal: subtotal,
                      platformFee: platformFee, // SCRUM-337: Standardized terminology
                      taxAmountCents: taxAmountCents,
                      total: totalPrice,
                    }}
                  />
                  {/* Navigation buttons for Stage 6 - SCRUM-340: Fixed button colors */}
                  <div className="mt-6 flex gap-4">
                    <button
                      onClick={handleBack}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => {
                        // Validate checkboxes before proceeding
                        if (!bookingData.termsAccepted || !bookingData.policyAccepted) {
                          setStageError('Please accept the terms and policies to continue');
                          return;
                        }
                        handleNext({});
                      }}
                      disabled={!bookingData.termsAccepted || !bookingData.policyAccepted || stageLoading || !!quoteError}
                      className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-300"
                    >
                      {stageLoading ? 'Saving...' : 'Continue to Payment'}
                    </button>
                  </div>
                </div>
              ) : currentStage === 7 ? (
                <CurrentStageComponent
                  bookingData={bookingData}
                  onNext={handleNext}
                  onBack={handleBack}
                  trackProgressAction={trackProgressAction}
                  totalPrice={totalPrice}
                  quoteId={quoteId}
                />
              ) : (
                <CurrentStageComponent
                  bookingData={bookingData}
                  minimumAge={bookingData.minimumAge}
                  participantTypes={quoteData?.participantTypes || []}
                  onNext={handleNext}
                  onBack={handleBack}
                  trackProgressAction={trackProgressAction}
                  onEditStage={handleStageClick}
                />
              )
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">
                  {stages[currentStage - 1]?.name}
                </h2>
                <p className="text-gray-600">This stage is not yet implemented.</p>
                <div className="mt-6 flex gap-4">
                  {currentStage > 1 && (
                    <button
                      onClick={handleBack}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Back
                    </button>
                  )}
                  {currentStage < stages.length && (
                    <button
                      onClick={() => handleNext({})}
                      className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                    >
                      Continue
                    </button>
                  )}
                </div>
              </div>
            )}
            </div>{/* close relative wrapper */}
          </div>

          {/* Pricing Summary - SCRUM-337, SCRUM-339: Experience context + pricing */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              {/* SCRUM-339: Experience Context Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Your Experience</h3>
                <h4 className="font-medium text-gray-800 mb-2">
                  {searchParams.get('title') || 'Experience'}
                </h4>
                {bookingData.date && (
                  <p className="text-sm text-gray-600 mb-1">
                    {bookingData.endDate && bookingData.endDate !== bookingData.date
                      ? `📅 ${new Date(bookingData.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${new Date(bookingData.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : `📅 ${new Date(bookingData.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`
                    }
                  </p>
                )}
                <p className="text-sm text-gray-600">
                  👥 {guests} {guests === 1 ? 'guest' : 'guests'}
                </p>
              </div>

              {/* SCRUM-1279: PriceBreakdown with structured quote data (AC-01) */}
              {quoteData && (
                <PriceBreakdown
                  pricing={{
                    basePriceCents: quoteData.baseRateCents || quoteData.basePriceCents || 0,
                    modifiers: quoteData.modifiers || [],
                    addons: quoteData.addons || [],
                    promotion: quoteData.promotion || null,
                    promotionDiscountCents: quoteData.promotionDiscountCents || 0,
                    subtotalCents: quoteData.subtotalCents || 0,
                    platformFeeCents: quoteData.platformFeeCents || 0,
                    platformFeePercent: quoteData.platformFeePercent || 8,
                    taxAmountCents: taxAmountCents || 0,
                    totalCents: (quoteData.totalCents || 0) + (taxAmountCents || 0),
                  }}
                  partySize={guests}
                  defaultExpanded={false}
                />
              )}

              {/* Fallback: PricingSummary shown while quote loads */}
              {!quoteData && (
                <PricingSummary
                  perPersonCents={pricePerPerson}
                  numParticipants={guests}
                  subtotalCents={subtotal}
                  platformFeeCents={platformFee}
                  taxAmountCents={taxAmountCents}
                  totalCents={totalPrice + taxAmountCents}
                />
              )}

              {/* SCRUM-1279: PromoCodeInput — "Have a promo code?" collapsed pattern (AC-02) */}
              {quoteData && (
                <PromoCodeInput
                  experienceId={bookingData.experienceId}
                  bookingValueCents={quoteData.totalCents || 0}
                  tripDate={bookingData.startDate || bookingData.date}
                  onPromoApplied={handlePromoApplied}
                  onPromoRemoved={handlePromoRemoved}
                  autoAppliedPromotion={autoAppliedPromo}
                  autoAppliedSavings={autoAppliedSavings}
                />
              )}

              {/* SCRUM-1279: Add-ons placeholder (AC-07) */}
              <div className="text-sm text-gray-500 mt-2">
                Add-ons available — coming soon
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SCRUM-1279: PriceSummaryBar — mobile sticky bar (AC-04) */}
      <PriceSummaryBar
        totalCents={totalPrice}
        pricePerPersonCents={pricePerPerson}
        partySize={guests}
        onBookNow={() => {
          // Scroll to current stage CTA
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        loading={quoteLoading}
        bookNowLabel={currentStage === 7 ? 'Complete Booking' : 'Continue'}
        disabled={!!quoteError}
      />
      <PriceSummaryBarSpacer />

      {/* TODO: Add "Still Interested?" modal for hold extension */}
    </div>
  );
}
